import React, { useEffect } from 'react'
import { useSelector, useDispatch, useStore } from 'react-redux'
import { moduleContext } from 'react-contextual'
import { walletApiEvents, walletApiSelectors } from '../api/wallet'
import { portfolioEvents, portfolioSelectors } from '../portfolios/state'
import { path } from 'ramda'
import { portfolioApiSelectors } from '../api/portfolio'
import { partyApiEvents, partyApiSelectors } from '../api/party'
import { accountingApiEvents, accountingApiSelectors } from '../api/accounting'
import { RootState } from 'src/state/types'
import { $TSFixMe } from 'types/ts-migrate'
import { TDepositTransferRequest } from 'modules/api/wallet/types'

interface DepositProviderProps {
  context: React.Context<$TSFixMe>
  children: React.ReactNode
  currentProperty?: Record<string, unknown>
  portfolioViewId?: string
  [key: string]: $TSFixMe
}

interface TransferPayload {
  body: TDepositTransferRequest
  params: { partyId?: string }
}

const DepositProvider = moduleContext()(
  ({ context, children, currentProperty, portfolioViewId, ...props }: DepositProviderProps) => {
    const store = useStore()
    const dispatch = useDispatch()
    const state = store.getState()
    const getWalletAsset = (partyId?: string | null, accountId?: string | null) =>
      walletApiSelectors.getBalanceAsset(state)(partyId, accountId, 'Tenant')
    // Derive the tenant + deposit account straight from the lease id in the URL
    // (portfolioViewId), read via useSelector, mirroring PortfolioView. The previous approach
    // indexed viewData.portfolios via the selectedPortfolio prop, which depends on the
    // property's portfolio list being loaded — on a hard reload that list isn't ready yet so
    // findIndex returned -1, selectedPortfolioId was undefined, and balance_request never
    // fired. Keying off portfolioViewId removes that dependency; useSelector also makes the
    // provider re-render when the tenant party + accounts load asynchronously after mount.
    const selectedPortfolioTenantId = useSelector(state =>
      portfolioApiSelectors.getPrimaryTenantByPortfolioId(state)(portfolioViewId),
    )

    const selectedTenantAccountId = useSelector(state => {
      const account = partyApiSelectors.getPartyAccountByPortfolioId(state)(
        selectedPortfolioTenantId,
        portfolioViewId,
        'Tenant',
      )
      return account?.accountId
    })

    const depositAccountBalance = useSelector(state =>
      walletApiSelectors.getDepositAccountBalance(state)(selectedPortfolioTenantId, selectedTenantAccountId),
    )

    useEffect(() => {
      if (selectedPortfolioTenantId) {
        dispatch(walletApiEvents.balance_request({ partyId: selectedPortfolioTenantId }))
        // Ensure the full party entity (with accounts[]) is loaded for this tenant.
        // The party reducer now merges bulk parties_success over existing entities, so a
        // late bulk fetch no longer strips accounts; this individual fetch is kept to
        // guarantee accounts are present even if the portfolio prefetch hasn't run yet.
        dispatch(partyApiEvents.party_request(selectedPortfolioTenantId))
      }
    }, [selectedPortfolioTenantId, dispatch])

    useEffect(() => {
      if (selectedPortfolioTenantId && selectedTenantAccountId) {
        dispatch(
          accountingApiEvents.depositStatementJson_request({
            id: selectedPortfolioTenantId,
            accountId: selectedTenantAccountId,
          }),
        )
      }
    }, [selectedPortfolioTenantId, selectedTenantAccountId, dispatch])

    const depositStore = {
      currentProperty,
      depositAccountBalance,
      /**
       * @todo clean this up, should fetch asset before opening transfer modal?
       */
      depositTransferModal: useSelector(portfolioSelectors.depositTransferModal),
      openDepositTransferModal: () => {
        const selectedTenantAccountAsset = path(
          ['name'],
          getWalletAsset(selectedPortfolioTenantId, selectedTenantAccountId),
        )
        if (selectedTenantAccountAsset) {
          dispatch(
            portfolioEvents.openDepositTransferModal({
              partyId: selectedPortfolioTenantId,
              account: selectedTenantAccountId,
              asset: selectedTenantAccountAsset,
            }),
          )
        }
      },
      handleTransfer: ({ body, params }: TransferPayload) => {
        // `partyId` is a path segment on TRANSFER; buildEndpoint throws without it, which
        // happens inside the epic and tears the stream down.
        if (!params.partyId) {
          return
        }
        dispatch(
          walletApiEvents.transfer_request({
            body,
            params: { partyId: params.partyId },
            meta: {
              partyId: selectedPortfolioTenantId ?? undefined,
              amount: body.amount,
              account: body.account,
            },
          }),
        )
      },
      closeDepositTransferModal: () => dispatch(portfolioEvents.closeDepositTransferModal()),
      depositStatement: useSelector<RootState>(state =>
        accountingApiSelectors.getDepositStatementJsonById(state)(selectedTenantAccountId as string),
      ),

      // Deposit topup request
      openDepositTopupRequest: (portfolioId: string) =>
        dispatch(portfolioEvents.depositTopupRequestOpened(portfolioId)),
      ...props,
    }
    return <context.Provider value={depositStore}>{children}</context.Provider>
  },
)

export default DepositProvider
